import { useState, useEffect } from 'react';
import { getMatchingScore } from '../util/api';
import { resolveStatusByMatchingMatrixScore, SCORE_INSUFFICIENT } from '../util/user';

/**
 * A score only changes when one of the two listings behind it changes, so it is
 * safe to hold on to for a while. The server keeps the same figure cached for
 * five minutes, so matching that here costs no extra staleness.
 */
const SCORE_CACHE_TTL = 5 * 60 * 1000;

const scoreCache = Object.create(null);
const pendingRequests = Object.create(null);

const resolveMatchingScore = cacheKey => {
    const cached = scoreCache[cacheKey];

    if (cached && Date.now() - cached.fetchedAt < SCORE_CACHE_TTL) {
        return Promise.resolve(cached.score);
    }

    // Every card on a results grid asks for its own score, and the grid is
    // remounted on every search. Reuse the request that is already on its way.
    if (pendingRequests[cacheKey]) {
        return pendingRequests[cacheKey];
    }

    const [riderListingId, horseOwnerListingId] = cacheKey.split(',');

    const request = getMatchingScore({ riderListingId, horseOwnerListingId })
        .then(scoreRes => {
            const scorePalette =
                scoreRes && scoreRes.finalScore
                    ? resolveStatusByMatchingMatrixScore(scoreRes.finalScore)
                    : SCORE_INSUFFICIENT;

            const score = { ...scoreRes, scorePalette };

            scoreCache[cacheKey] = { score, fetchedAt: Date.now() };

            return score;
        })
        .finally(() => {
            delete pendingRequests[cacheKey];
        });

    pendingRequests[cacheKey] = request;

    return request;
};

export const useMatchingMatrix = ({ riderListingId, horseOwnerListingId }) => {
    const [fulfilled, setFulfilled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [matchingScore, setMatchingScore] = useState(null);
    const [params, setParams] = useState(null);

    const reset = loadingFlag => {
        setMatchingScore(null);
        setLoading(loadingFlag);
    };

    const onGettingMatchingScore = async cacheKey => {
        reset(true);

        try {
            setMatchingScore(await resolveMatchingScore(cacheKey));
        } catch (e) {
            // to do
        } finally {
            setLoading(false);
            setFulfilled(true);
        }
    };

    useEffect(() => {
        if (!riderListingId || !horseOwnerListingId) {
            return reset(false);
        }
        const paramsStr = [riderListingId, horseOwnerListingId].join(',');
        if (paramsStr === params) return;
        setParams(paramsStr);
    }, [riderListingId, horseOwnerListingId]);

    /**
     * It gives an incorrect score, e.g.
     * on Transaction page when a rider sends a request to a horseowner,
     * there is a condition: fulfilled is 'true' and loading is also 'true'
     */

    // useEffect(() => {
    //     if (fulfilled) {
    //         return;
    //     }
    //     setFulfilled(loading);
    // }, [loading]);

    useEffect(() => {
        if (!params) return;

        onGettingMatchingScore(params);
    }, [params]);

    return [loading, matchingScore, fulfilled];
};
